import { useCallback, useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'

interface ListboxNavOptions {
  onSelect: (index: number) => void
  onClose: () => void
  /** When this changes truthy, the active index resets to 0 (dropdown opened). */
  open?: boolean
}

/**
 * Keyboard navigation for a flat listbox of `count` options. Arrow keys move with
 * wrap-around, Home/End jump to the ends, Enter/Space selects, Escape closes.
 */
export function useListboxNav(count: number, { onSelect, onClose, open }: ListboxNavOptions) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (open) setActiveIndex(0)
  }, [open])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex((i) => (count === 0 ? 0 : (i + 1) % count))
          break
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex((i) => (count === 0 ? 0 : (i - 1 + count) % count))
          break
        case 'Home':
          e.preventDefault()
          setActiveIndex(0)
          break
        case 'End':
          e.preventDefault()
          setActiveIndex(count - 1)
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          onSelect(activeIndex)
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [count, activeIndex, onSelect, onClose],
  )

  return { activeIndex, setActiveIndex, handleKeyDown }
}
